import React from "react";
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  withStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import dataforms from "../../service/dataforms";
import { linkedIdentifier } from "../../libs/formLink";
import { formatCurrency, formatQuantity } from "../../libs/format";

const PRODUCT_TYPES = ["product-order", "product-table"];

// Extrai o valor "exibível" de um item salvo do data form (por índice).
const displayValue = (entry) => {
  if (!entry) return "";
  // checkbox salva um array `options`; mostra as marcadas (+ observações).
  if (Array.isArray(entry.options)) {
    const checked = entry.options
      .filter((o) => o && o.checked)
      .map((o) => o.label);
    const obs = entry.options
      .filter((o) => o && o.type === "obs" && o.value)
      .map((o) => o.value);
    return checked.concat(obs).join(", ");
  }
  if (entry.value !== undefined && entry.value !== null) {
    // select-resp salva um array de usuários; mostra os nomes.
    if (Array.isArray(entry.value)) {
      return entry.value.map((u) => (u && u.name) || "").join(", ");
    }
    // select-func salva um objeto usuário; mostra o nome.
    if (typeof entry.value === "object") return entry.value.name || "";
    return String(entry.value);
  }
  return "";
};

// Campo de "Vínculo de Formulário": seleciona um preenchimento (DataForm) de
// outro formulário pelo identificador e traz campos dele AO VIVO (reconsulta).
// Só a referência (target_data_form_id) é persistida.
// `onValuesLoaded(values, { userChanged })` avisa o pai a cada carga dos valores
// do registro vinculado — é o gancho da cópia de itens (libs/linkPrefill.js);
// `userChanged` distingue a troca de registro feita pelo usuário da carga ao
// abrir o preenchimento.
// Props: field, onChange(patch), onValuesLoaded?, readOnly?
class FormLinkField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dataForms: [], // opções do seletor
      values: null, // valores do data form selecionado (ao vivo)
      targetLayout: null, // layout do form-alvo (para rótulos das colunas de tabela)
      loading: false,
    };
    // O que já foi carregado. Não dá para comparar prevProps.field com
    // this.props.field: o AddDialogForm muta os objetos do layout no lugar
    // (mesma referência), então prev e curr seriam sempre iguais.
    this.loadedTargetFormId = null;
    this.loadedDataFormId = null;
  }

  componentDidMount() {
    this.syncFromField();
  }

  // No editar, o AddDialogForm monta o campo com o layout "cru" e só depois
  // mescla os dados salvos (target_data_form_id etc.) — sem isto, os valores
  // do registro vinculado nunca eram carregados ao reabrir o preenchimento.
  componentDidUpdate() {
    this.syncFromField();
  }

  syncFromField = () => {
    const { field } = this.props;
    const targetFormId = field.target_form_id || null;
    if (targetFormId !== this.loadedTargetFormId) {
      this.loadTargetForm();
    }
    const dataFormId = field.target_data_form_id || null;
    if (dataFormId !== this.loadedDataFormId) {
      this.loadedDataFormId = dataFormId;
      if (dataFormId) {
        this.loadValues(dataFormId);
      } else {
        this.setState({ values: null });
      }
    }
  };

  loadTargetForm = () => {
    const { field } = this.props;
    this.loadedTargetFormId = field.target_form_id || null;
    if (!field.target_form_id) return;
    dataforms
      .listDataForms(field.target_form_id)
      .then((list) => {
        this.setState({ dataForms: list || [] });
        // Preenchido por automação (só o id): resolve o identificador para
        // exibição/persistência.
        const curr = this.props.field;
        if (curr.target_data_form_id && !linkedIdentifier(curr)) {
          const found = (list || []).find(
            (d) => d.id === curr.target_data_form_id
          );
          if (found && found.identifier) {
            this.props.onChange({ target_identifier: found.identifier });
          }
        }
      })
      .catch(() => {});
    dataforms
      .getFormLayout(field.target_form_id)
      .then((layout) => this.setState({ targetLayout: layout || [] }))
      .catch(() => {});
  };

  loadValues = (dataFormId, { userChanged = false } = {}) => {
    this.loadedDataFormId = dataFormId;
    this.setState({ loading: true });
    dataforms
      .getDataFormValues(dataFormId)
      .then((values) => {
        // Resposta de um registro que já não é o selecionado: descarta.
        if (this.loadedDataFormId !== dataFormId) return;
        this.setState({ values: values || [], loading: false });
        this.autoSelectSingleRows(values || []);
        if (this.props.onValuesLoaded) {
          this.props.onValuesLoaded(values || [], { userChanged });
        }
      })
      .catch(() => this.setState({ values: [], loading: false }));
  };

  // Campos tabela com uma única linha já vêm marcados.
  autoSelectSingleRows = (values) => {
    const { field } = this.props;
    const patch = { ...(field.table_selections || {}) };
    let changed = false;
    (field.fields || []).forEach((f) => {
      if (f.type === "table" && patch[f.index] === undefined) {
        const body = (values[f.index] && values[f.index].body) || [];
        if (body.length === 1) {
          patch[f.index] = 0;
          changed = true;
        }
      }
    });
    if (changed) this.props.onChange({ table_selections: patch });
  };

  handleTableRowSelect = (fieldIndex, rowIndex) => {
    this.props.onChange({
      table_selections: {
        ...(this.props.field.table_selections || {}),
        [fieldIndex]: rowIndex,
      },
    });
  };

  handleSelect = (event, dataForm) => {
    if (!dataForm) {
      this.props.onChange({ target_data_form_id: null, target_identifier: null });
      this.setState({ values: null });
      return;
    }
    // `target_identifier`, nunca `identifier`: esta última é a flag do layout
    // (ver libs/formLink.js).
    this.props.onChange({
      target_data_form_id: dataForm.id,
      target_identifier: dataForm.identifier,
    });
    this.loadValues(dataForm.id, { userChanged: true });
  };

  selectedOption = () => {
    const { field } = this.props;
    if (!field.target_data_form_id) return null;
    const found = this.state.dataForms.find(
      (d) => d.id === field.target_data_form_id
    );
    return (
      found || {
        id: field.target_data_form_id,
        identifier: linkedIdentifier(field),
      }
    );
  };

  // Campo trazido do tipo tabela: seletor de linha (Lançamentos) + colunas da linha.
  renderTableField = (f) => {
    const { classes, field, readOnly } = this.props;
    const entry = (this.state.values || [])[f.index];
    const body = (entry && entry.body) || [];
    const layoutField = (this.state.targetLayout || [])[f.index];
    const columns = (layoutField && layoutField.columns) || [];
    const sel = field.table_selections
      ? field.table_selections[f.index]
      : undefined;
    const effective = sel != null ? sel : body.length === 1 ? 0 : "";
    const row = effective !== "" ? body[effective] : null;

    return (
      <div className={classes.tableField}>
        {!readOnly && body.length > 0 && (
          <FormControl className={classes.rowSelect}>
            <InputLabel>Selecione a linha</InputLabel>
            <Select
              value={effective}
              onChange={(e) => this.handleTableRowSelect(f.index, e.target.value)}
            >
              {body.map((r, ri) => (
                <MenuItem key={ri} value={ri}>
                  {r.map((c) => (c == null ? "" : c)).join(" · ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {row && (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableBody>
                {row.map((cell, ci) => (
                  <TableRow key={ci}>
                    <TableCell className={classes.labelCell}>
                      {(columns[ci] &&
                        columns[ci].component &&
                        columns[ci].component.label) ||
                        `Coluna ${ci + 1}`}
                    </TableCell>
                    <TableCell>{cell == null ? "" : cell}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    );
  };

  // Campo trazido do tipo produtos: tabela curta dos itens. Preço só com
  // `show_prices` no campo trazido (configurado no construtor).
  renderItemsField = (f) => {
    const { classes } = this.props;
    const entry = (this.state.values || [])[f.index];
    const items = (entry && entry.items) || [];
    if (this.state.loading) return <Typography variant="body2">...</Typography>;
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          Nenhum produto.
        </Typography>
      );
    }
    return (
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell align="right">Quantidade</TableCell>
              <TableCell align="right">Total</TableCell>
              {f.show_prices && <TableCell align="right">Valor total</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, i) => (
              <TableRow key={`${item.product_id}-${i}`}>
                <TableCell>{item.product_name}</TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
                <TableCell align="right">
                  {formatQuantity(item.total_quantity)}
                  {item.unit_of_measure ? ` ${item.unit_of_measure}` : ""}
                </TableCell>
                {f.show_prices && (
                  <TableCell align="right">
                    {item.total_value == null
                      ? "-"
                      : formatCurrency(item.total_value, item.currency)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  renderBroughtValue = (f) => {
    if (f.type === "table") return this.renderTableField(f);
    if (PRODUCT_TYPES.includes(f.type)) return this.renderItemsField(f);
    return (
      <Typography variant="body2">
        {this.state.loading ? "..." : displayValue((this.state.values || [])[f.index])}
      </Typography>
    );
  };

  render() {
    const { classes, field, readOnly } = this.props;
    const broughtFields = field.fields || [];
    // Vínculo criado por automação de botão de ação: o registro de origem é
    // fixo (somente leitura), mas a seleção de linha de tabela continua livre.
    const locked = !!field.locked;

    return (
      <div className={classes.container}>
        <Typography variant="subtitle1" className={classes.title}>
          {field.component ? field.component.label : "Vínculo"}
          {field.target_form_name ? ` (${field.target_form_name})` : ""}
        </Typography>

        {!readOnly && !locked && (
          <Autocomplete
            className={classes.select}
            options={this.state.dataForms}
            value={this.selectedOption()}
            getOptionLabel={(o) =>
              o ? `${o.id} - ${o.identifier || "(sem identificador)"}` : ""
            }
            getOptionSelected={(o, v) => o.id === (v && v.id)}
            onChange={this.handleSelect}
            renderInput={(params) => (
              <TextField {...params} label="Selecione o registro" />
            )}
          />
        )}

        {(readOnly || locked) && (
          <Typography variant="body2" color="textSecondary">
            {this.selectedOption()
              ? `${this.selectedOption().id} - ${
                  this.selectedOption().identifier || ""
                }`
              : "Nenhum registro vinculado"}
          </Typography>
        )}

        {field.target_data_form_id && broughtFields.length > 0 && (
          <div className={classes.brought}>
            {broughtFields.map((f) => (
              <div key={f.index} className={classes.broughtField}>
                <Typography variant="body2" className={classes.broughtLabel}>
                  <b>{f.label}</b>
                </Typography>
                {this.renderBroughtValue(f)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

const styles = (theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
  },
  select: {
    maxWidth: 420,
    margin: theme.spacing(1, 0),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
    maxWidth: 520,
  },
  labelCell: {
    width: 180,
    whiteSpace: "nowrap",
  },
  brought: {
    marginTop: theme.spacing(1),
  },
  broughtField: {
    marginBottom: theme.spacing(2),
  },
  broughtLabel: {
    marginBottom: theme.spacing(0.5),
  },
  tableField: {
    marginTop: theme.spacing(0.5),
  },
  rowSelect: {
    minWidth: 320,
    maxWidth: 520,
    marginBottom: theme.spacing(1),
  },
});

export default withStyles(styles)(FormLinkField);
